import axios from 'axios';
import { doc, updateDoc } from 'firebase/firestore';
import { createCloudRunAxios } from './axios';
import { db } from '../lib/firebase';

const remiApi = createCloudRunAxios('remi-api');

/**
 * Extract a human-readable error message from an Axios error response.
 * FastAPI returns errors as `{ detail: "..." }`, so we pull that out
 * and re-throw a plain Error so callers can match on `error.message`.
 */
export function rethrowWithDetail(err: unknown): never {
  if (axios.isAxiosError(err)) {
    const detail = err.response?.data?.detail;
    if (typeof detail === 'string') {
      throw new Error(detail);
    }
  }
  throw err;
}

/**
 * Map frontend ContactFormData fields to Remi API snake_case schema.
 * Only sends fields the API recognizes — drops unknown keys.
 */
const mapContactFormToApi = (
  data: Record<string, unknown>
): Record<string, unknown> => {
  const mapped: Record<string, unknown> = {};

  // Required
  if (data.organization_id) mapped.organization_id = data.organization_id;

  // Personal info
  if (data.firstName !== undefined) mapped.first_name = data.firstName;
  if (data.lastName !== undefined) mapped.last_name = data.lastName;
  if (data.email !== undefined) mapped.email = data.email;
  if (data.phoneNumber !== undefined) mapped.phone = data.phoneNumber;
  if (data.phoneStatus !== undefined) mapped.phone_status = data.phoneStatus;

  // Address (subfields match)
  if (data.address !== undefined) mapped.address = data.address;

  // Tags
  if (data.tags !== undefined) mapped.tags = data.tags;

  // Opt-in fields — top-level API fields
  if (data.opt_in !== undefined) mapped.opt_in = data.opt_in;
  if (data.opt_in_date !== undefined && data.opt_in_date !== '') {
    const dateStr = data.opt_in_date as string;
    // Ensure full ISO string (form stores YYYY-MM-DD, API expects ISO datetime)
    mapped.opt_in_date = dateStr.includes('T') ? dateStr : `${dateStr}T00:00:00Z`;
  }
  if (data.opt_in_desc !== undefined) mapped.opt_in_desc = data.opt_in_desc;

  // Source (UI "Source" — maps to API lead_source; leadSourceSecondary is set by Speculo campaigns and not editable here)
  if (data.leadSource !== undefined) mapped.lead_source = data.leadSource;

  // Test contact flag
  if (data.call_tester !== undefined) mapped.is_test_contact = data.call_tester;

  // Claimed lead
  if (data.claimedLead !== undefined) mapped.claimed_lead = data.claimedLead ? toSnakeCaseKeys(data.claimedLead) : null;
  if (data.claimedLeadSource !== undefined) mapped.claimed_lead_source = data.claimedLeadSource;

  // Assigned agent (CRM owner) — scalar FK + denormalized snapshot so list
  // surfaces can render name/phone without a client-side agent lookup.
  if (data.assignedAgentId !== undefined) mapped.assigned_agent_id = data.assignedAgentId;
  if (data.assignedPondId !== undefined) mapped.assigned_pond_id = data.assignedPondId;
  if (data.assignedAgent !== undefined) {
    mapped.assigned_agent = data.assignedAgent
      ? toSnakeCaseKeys(data.assignedAgent)
      : null;
  }

  // Pass through any already-snake_case fields (for direct API calls)
  const passthroughKeys = [
    'first_name', 'last_name', 'phone', 'phone_status', 'email_status',
    'lead_source', 'lead_score', 'intent_level', 'status', 'lifecycle_stage',
    'recent_outcome', 'claimed_lead', 'claimed_lead_source', 'flywheel_opt_out', 'flywheel_opt_out_at',
    'ai_paused', 'crm_source', 'crm_id', 'assigned_agent_id', 'assigned_pond_id', 'custom_fields',
    'metadata', 'cadence_path', 'opt_in', 'opt_in_date', 'opt_in_desc', 'valuation_requested', 'is_test_contact',
  ];
  for (const key of passthroughKeys) {
    if (data[key] !== undefined && mapped[key] === undefined) {
      mapped[key] = data[key];
    }
  }

  // Convert empty strings to null so the API clears the field instead of ignoring it
  for (const key of Object.keys(mapped)) {
    if (mapped[key] === '') {
      mapped[key] = null;
    }
  }

  return mapped;
};

/**
 * Create a new contact
 */
export const createContact = async (data: Record<string, unknown>) => {
  try {
    const payload = mapContactFormToApi(data);
    const response = await remiApi.post('/api/v1/contacts', payload);
    return response.data;
  } catch (err) {
    rethrowWithDetail(err);
  }
};

/**
 * Update an existing contact (partial update)
 */
export const updateContact = async (
  contactId: string,
  organizationId: string,
  data: Record<string, unknown>
) => {
  try {
    const payload = mapContactFormToApi(data);
    const response = await remiApi.put(`/api/v1/contacts/${contactId}`, payload, {
      params: { organization_id: organizationId },
    });
    return response.data;
  } catch (err) {
    rethrowWithDetail(err);
  }
};

/**
 * Opt a contact out of communications.
 * Sets opt_in=False and flywheel_opt_out=True, then cancels active FlowExecutions.
 */
export const optOutContact = async (contactId: string, organizationId: string) => {
  const response = await remiApi.post(`/api/v1/contacts/${contactId}/opt-out`, null, {
    params: { organization_id: organizationId },
  });
  return response.data;
};

interface RegenerateFollowUpPlanResponse {
  accepted: boolean;
  request_id: string;
  contact_id: string;
  organization_id: string;
  cooldown_seconds: number;
}

// SPEC-1710: queue a Flywheel follow-up plan rebuild. 202 async — the new
// steps land on Contact.followUpPlan on a later read. Org scoping is taken
// from the auth token, so no body/params. 429 when one is already in flight.
export const regenerateFollowUpPlan = async (
  contactId: string
): Promise<RegenerateFollowUpPlanResponse> => {
  try {
    const response = await remiApi.post(
      `/api/v1/contacts/${contactId}/follow-up-plan/regenerate`
    );
    return response.data;
  } catch (err) {
    rethrowWithDetail(err);
  }
};

/**
 * Soft-delete a contact
 */
export const deleteContact = async (contactId: string, organizationId: string) => {
  const response = await remiApi.delete(`/api/v1/contacts/${contactId}`, {
    params: { organization_id: organizationId },
  });
  return response.data;
};

/**
 * Recursively convert all keys of an object from camelCase to snake_case.
 * Arrays are mapped element-by-element. Primitives are returned as-is.
 * Applied to all org payloads so callers can use camelCase internally
 * and the API always receives snake_case.
 */
const toSnakeCaseKeys = (data: unknown): unknown => {
  if (Array.isArray(data)) {
    return data.map(toSnakeCaseKeys);
  }
  if (data !== null && typeof data === 'object') {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
      const snakeKey = key.replace(/([A-Z])/g, '_$1').toLowerCase();
      result[snakeKey] = toSnakeCaseKeys(value);
    }
    return result;
  }
  return data;
};

/**
 * Create a new organization
 */
export const createOrganization = async (data: Record<string, unknown>) => {
  const response = await remiApi.post('/api/v1/organizations', toSnakeCaseKeys(data));
  return response.data;
};

/**
 * Turn the Follow-up Flywheel on or off for an organization.
 *
 * A dedicated endpoint rather than a settings write: the server owns whatever
 * else enabling entails, so the client sends only the intent.
 */
export const setOrganizationFlywheel = async (organizationId: string, enabled: boolean) => {
  try {
    const response = await remiApi.post(`/api/v1/organizations/${organizationId}/flywheel`, {
      enabled,
    });
    return response.data;
  } catch (err) {
    rethrowWithDetail(err);
  }
};

/**
 * Update an existing organization (partial update)
 */
export const updateOrganization = async (
  organizationId: string,
  data: Record<string, unknown>
) => {
  // Write to Remi API (Spanner) — expects snake_case
  const response = await remiApi.put(`/api/v1/organizations/${organizationId}`, toSnakeCaseKeys(data));

  const firestoreData = { ...data };
  const canonicalCallSettings = response.data?.organization?.call_settings;
  if (canonicalCallSettings && typeof canonicalCallSettings === 'object') {
    firestoreData.call_settings = canonicalCallSettings;
  }

  // Preserve original casing for unrelated fields, but mirror the backend's canonical
  // call_settings so Eventarc cannot reintroduce a provider voice ID.
  // Non-blocking: log but don't fail the whole update if Firestore write errors.
  try {
    const orgRef = doc(db, 'Organizations', organizationId);
    await updateDoc(orgRef, firestoreData);
  } catch (firestoreError) {
    console.warn('Firestore Organizations dual-write failed (Spanner update succeeded):', firestoreError);
  }

  return response.data;
};

/**
 * Create a new knowledge base for an organization
 */
export const createKnowledgeBase = async (
  organizationId: string,
  data: Record<string, unknown>
) => {
  const response = await remiApi.post(
    `/api/v1/organizations/${organizationId}/knowledge-bases`,
    data
  );
  return response.data;
};

/**
 * Update an existing knowledge base (partial update)
 */
export const updateKnowledgeBase = async (
  organizationId: string,
  knowledgeBaseId: string,
  data: Record<string, unknown>
) => {
  const response = await remiApi.put(
    `/api/v1/organizations/${organizationId}/knowledge-bases/${knowledgeBaseId}`,
    data
  );
  return response.data;
};

/**
 * Delete a knowledge base
 */
export const deleteKnowledgeBase = async (
  organizationId: string,
  knowledgeBaseId: string
) => {
  const response = await remiApi.delete(
    `/api/v1/organizations/${organizationId}/knowledge-bases/${knowledgeBaseId}`
  );
  return response.data;
};

/**
 * Create a new flow for an organization
 */
export const createFlow = async (
  organizationId: string,
  data: Record<string, unknown>
) => {
  const response = await remiApi.post(
    `/api/v1/organizations/${organizationId}/flows`,
    data
  );
  return response.data;
};

/**
 * Update an existing flow (partial update)
 */
export const updateFlow = async (
  organizationId: string,
  flowId: string,
  data: Record<string, unknown>
) => {
  const response = await remiApi.put(
    `/api/v1/organizations/${organizationId}/flows/${flowId}`,
    data
  );
  return response.data;
};

/**
 * Soft-delete a flow (sets deleted_at timestamp)
 */
export const deleteFlow = async (organizationId: string, flowId: string) => {
  const response = await remiApi.delete(
    `/api/v1/organizations/${organizationId}/flows/${flowId}`
  );
  return response.data;
};

/**
 * Pause a flow and all non-terminal executions
 */
export const pauseFlow = async (flowId: string) => {
  const response = await remiApi.post(`/api/v1/flows/${flowId}/pause`);
  return response.data;
};

/**
 * Resume a paused flow, moving executions back to draft
 */
export const resumeFlow = async (flowId: string) => {
  const response = await remiApi.post(`/api/v1/flows/${flowId}/resume`);
  return response.data;
};

/**
 * Archive a flow definition and cancel all executions
 */
export const archiveFlow = async (flowId: string) => {
  const response = await remiApi.post(`/api/v1/flows/${flowId}/archive`);
  return response.data;
};

/**
 * Activate all draft FlowExecutions for a flow (start the flow)
 */
export const activateFlow = async (flowId: string) => {
  const response = await remiApi.post(`/api/v1/flows/${flowId}/activate`);
  return response.data;
};

/**
 * Create a note for a contact
 */
export const createContactNote = async (
  contactId: string,
  organizationId: string,
  data: {
    body: string;
    authorId?: string;
    authorName?: string;
    authorType?: string;
    noteType?: string;
    subject?: string;
  }
): Promise<{ success: boolean; note_id: string; message: string }> => {
  const response = await remiApi.post(`/api/v1/contacts/${contactId}/notes`, {
    organization_id: organizationId,
    contact_id: contactId,
    body: data.body,
    author_id: data.authorId,
    author_name: data.authorName,
    author_type: data.authorType ?? 'agent',
    note_type: data.noteType ?? 'manual',
    subject: data.subject,
  });
  return response.data;
};

/**
 * Soft-delete a note for a contact
 */
export const deleteContactNote = async (
  contactId: string,
  noteId: string,
  organizationId: string
): Promise<void> => {
  await remiApi.delete(`/api/v1/contacts/${contactId}/notes/${noteId}`, {
    params: { organization_id: organizationId },
  });
};

/**
 * Partial-update a note. Only fields you pass get changed.
 */
export const updateContactNote = async (
  contactId: string,
  noteId: string,
  organizationId: string,
  data: { body?: string; subject?: string }
): Promise<{ success: boolean; note_id: string }> => {
  const response = await remiApi.put(
    `/api/v1/contacts/${contactId}/notes/${noteId}`,
    data,
    { params: { organization_id: organizationId } }
  );
  return response.data;
};

/**
 * Create a database upload request
 */
export const createDatabaseUpload = async (
  organizationId: string,
  data: {
    fileName: string;
    fileStoragePath: string;
    fileSizeBytes: number;
    mimeType: string;
    importSettings: Record<string, unknown>;
  }
) => {
  const response = await remiApi.post(`/api/v1/organizations/${organizationId}/database-uploads`, {
    file_name: data.fileName,
    file_storage_path: data.fileStoragePath,
    file_size_bytes: data.fileSizeBytes,
    mime_type: data.mimeType,
    import_settings: data.importSettings,
    notify_on_completion: true,
  });
  return response.data;
};

/**
 * Invite a user to the caller's organization (sends signup email)
 */
export const inviteUser = async (
  email: string,
  organizationId?: string,
  inviteData?: {
    firstName?: string;
    lastName?: string;
    phoneNumber?: string;
  }
) => {
  const response = await remiApi.post('/api/v1/users/invite', {
    email,
    ...(organizationId ? { organizationId } : {}),
    ...(inviteData?.firstName ? { first_name: inviteData.firstName } : {}),
    ...(inviteData?.lastName ? { last_name: inviteData.lastName } : {}),
    ...(inviteData?.phoneNumber ? { phone: inviteData.phoneNumber } : {}),
  });
  return response.data as { success: boolean; message: string | null; user_id: string | null; organization_id: string | null };
};

export interface BulkResendInviteItem {
  user_id: string;
  email: string | null;
  result: 'sent' | 'skipped' | 'failed';
  reason: string | null;
}

export interface BulkResendInvitesResponse {
  success: boolean;
  sent: number;
  skipped: number;
  failed: number;
  results: BulkResendInviteItem[];
}

/**
 * Resend invitation emails to pending (not yet registered) users in bulk.
 * Active/non-pending users are skipped server-side; per-user outcomes are
 * returned in `results`.
 */
export const resendInvitesBulk = async (userIds: string[], organizationId?: string) => {
  const response = await remiApi.post('/api/v1/users/invite/resend-bulk', {
    user_ids: userIds,
    ...(organizationId ? { organizationId } : {}),
  });
  return response.data as BulkResendInvitesResponse;
};

/**
 * Complete user signup from an invite link. Matches by email + org_id.
 * Send either `password` (backend creates/updates Firebase via Admin SDK) or
 * `firebase_uid` (backend links the existing Firebase UID, e.g. Google OAuth).
 */
export const signupUser = async (data: {
  email: string;
  org_id: string;
  first_name: string;
  last_name: string;
  phone?: string;
  password?: string;
  firebase_uid?: string;
}) => {
  try {
    const response = await remiApi.post('/api/v1/users/signup', data);
    return response.data as { success: boolean; message: string | null; user_id: string | null; organization_id: string | null };
  } catch (err) {
    rethrowWithDetail(err);
  }
};

/**
 * Delete or uninvite a user (removes Firebase Auth account if they have one)
 */
export const updateUser = async (
  userId: string,
  data: { firstName?: string; lastName?: string; email?: string; phone?: string; photoUrl?: string; bio?: string; slug?: string; preferences?: Record<string, unknown> }
) => {
  const payload: Record<string, unknown> = {};
  if (data.firstName !== undefined) payload.first_name = data.firstName;
  if (data.lastName !== undefined) payload.last_name = data.lastName;
  if (data.email !== undefined) payload.email = data.email;
  if (data.phone !== undefined) payload.phone = data.phone;
  if (data.photoUrl !== undefined) payload.photo_url = data.photoUrl;
  if (data.bio !== undefined) payload.bio = data.bio;
  if (data.slug !== undefined) payload.slug = data.slug;
  if (data.preferences !== undefined) payload.preferences = data.preferences;
  try {
    const response = await remiApi.put(`/api/v1/users/${userId}`, payload);
    return response.data;
  } catch (err) {
    rethrowWithDetail(err);
  }
};

export const deleteUser = async (identifier: string) => {
  const response = await remiApi.delete(`/api/v1/users/${identifier}`);
  return response.data as { success: boolean; message: string | null; user_id: string | null; organization_id: string | null };
};

/**
 * Send a password reset email via Remi API
 */
export const forgotPassword = async (email: string) => {
  const response = await remiApi.post('/api/v1/users/forgot-password', { email });
  return response.data;
};

/**
 * Validate that the authenticated user is active (called on login)
 */
export const validateUser = async () => {
  const response = await remiApi.post('/api/v1/users/validate');
  return response.data as { valid: boolean; reason: string | null; user: Record<string, unknown> | null };
};

/**
 * Get dashboard preferences from Remi API
 */
export const getDashboardPreferences = async (organizationId: string) => {
  const response = await remiApi.get('/api/v1/dashboard/preferences', {
    params: { organization_id: organizationId },
  });
  return response.data as Record<string, unknown> | null;
};

/**
 * Save dashboard preferences to Remi API
 */
export const saveDashboardPreferences = async (
  organizationId: string,
  preferences: Record<string, unknown>
) => {
  const response = await remiApi.put('/api/v1/dashboard/preferences', preferences, {
    params: { organization_id: organizationId },
  });
  return response.data;
};

/**
 * Get all saved dashboard blocks for a user/org
 */
export const getDashboardBlocks = async (organizationId: string) => {
  const response = await remiApi.get('/api/v1/dashboard/blocks', {
    params: { organization_id: organizationId },
  });
  // API may return array directly or { blocks: [...] } or { success, blocks: [...] }
  const data = response.data;
  if (Array.isArray(data)) return data as Array<Record<string, unknown>>;
  if (data?.blocks) return data.blocks as Array<Record<string, unknown>>;
  return [] as Array<Record<string, unknown>>;
};

/**
 * Create a saved dashboard block (pin a KPI widget)
 */
export const createDashboardBlock = async (
  organizationId: string,
  block: {
    type: string;
    title: string;
    payload: Record<string, unknown>;
    dataRef?: { entity: string; id: string; fields?: string[] };
  }
) => {
  const response = await remiApi.post('/api/v1/dashboard/blocks', {
    type: block.type,
    title: block.title,
    payload: block.payload,
    dataRefEntity: block.dataRef?.entity ?? null,
    dataRefId: block.dataRef?.id ?? null,
    dataRefFields: block.dataRef?.fields ?? [],
    organizationId: organizationId,
  });
  return response.data as Record<string, unknown>;
};

/**
 * Delete a saved dashboard block (unpin a KPI widget)
 */
export const deleteDashboardBlock = async (blockId: string) => {
  await remiApi.delete(`/api/v1/dashboard/blocks/${blockId}`);
};

/**
 * Bulk-assign contacts to a flow by contact IDs
 */
export const addContactsToFlowBulk = async (
  flowId: string,
  organizationId: string,
  contactIds: string[]
) => {
  const response = await remiApi.post(`/api/v1/flows/${flowId}/contacts/bulk`, {
    contact_ids: contactIds,
    organization_id: organizationId,
  });
  return response.data;
};
